import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Table } from '../../DataTable/Table';
import { ColumnDefinition } from '../../../types/table';
import { getAllTowerProperties } from '../../../constants/towerProperties';
import { debugLog } from '../../../constants/debug';
import { SchemaField } from '../../../types/project';
import { getDefaultUnit, getMeasureType } from '../../../utils/unitUtils';
import { processNodeData, findNodesByName, getNodesByParent, getAllNodesOfType } from '../../../utils/assetNodeUtils';
import type { TabComponentProps } from "../../../types/tabs"

export const LocationsTab: React.FC<TabComponentProps> = ({ selectedNode, projectData, menuAction }) => {
    debugLog("LocationsTab rendered with:", { selectedNode, projectData, menuAction })

    // Generate column definitions from schema
    const columns = useMemo(() => {
        const towerSchema = projectData?.schemas?.Tower;
        if (!towerSchema) {
            debugLog('No Tower schema found in projectData');
            return [];
        }

        debugLog('Generating columns with schema:', towerSchema);

        const allProperties = getAllTowerProperties(towerSchema);
        // Map the fields in the order defined in TOWER_PROPERTIES
        return allProperties.map(fieldKey => {
            // Find the schema entry that matches our property key
            const schemaEntries = Object.entries(towerSchema) as [string, SchemaField][];
            const matchingSchema = schemaEntries.find(([key]) => key === fieldKey);

            if (!matchingSchema) {
                debugLog(`No schema found for field: ${fieldKey}`);
                return null;
            }

            const [_, field] = matchingSchema;

            const defaultUnit = getDefaultUnit(field);

            const column = {
                key: fieldKey,
                title: field.title || fieldKey,
                unit: defaultUnit,
                measure: getMeasureType(defaultUnit),
                sortable: true,
                filterable: true,
                align: 'center' as const,
                type: field.type
            } as ColumnDefinition;

            debugLog('Created column definition:', {
                key: column.key,
                title: column.title,
                unit: column.unit,
                measure: column.measure,
                type: field.type,
                originalType: field.type
            });

            return column;
        }).filter((col): col is ColumnDefinition => col !== null);
    }, [projectData]);

    // Generate location data based on selection
    const locationData = useMemo(() => {
        debugLog('Generating location data with:', {
            selectedNode,
            hasProjectData: !!projectData,
            columnsLength: columns.length
        });

        if (!projectData || !columns.length) return [];

        // Handle tower selection
        if (selectedNode?.type === 'tower') {
            debugLog('Processing tower selection:', selectedNode.name);
            const towerData = findNodesByName(projectData, selectedNode.name, 'tower');
            debugLog('Found tower data:', towerData);

            // Ensure we only get the exact tower we want
            const exactTowerData = towerData.find(tower =>
                tower.name === selectedNode.name &&
                tower.type === 'tower'
            );

            debugLog('Filtered to exact tower:', exactTowerData);

            // Return array with single tower or empty array if not found
            return exactTowerData ? [processNodeData(exactTowerData, columns)] : [];
        }

        // Handle section selection
        if (selectedNode?.type === 'section') {
            debugLog('Processing section selection:', selectedNode.name);
            const sectionTowers = getNodesByParent(projectData, selectedNode.name, 'section', 'tower');
            debugLog('Found section towers:', sectionTowers);
            return sectionTowers.map(tower => processNodeData(tower, columns));
        }

        // Handle no selection or line selection
        if (!selectedNode || selectedNode.type === 'line') {
            debugLog('Processing all towers from all sections');
            const allTowers = getAllNodesOfType(projectData, 'tower');
            debugLog('Found all towers:', allTowers);

            // Create a Map to store unique towers by name
            const uniqueTowersMap = new Map();
            allTowers.forEach(tower => {
                if (!uniqueTowersMap.has(tower.name)) {
                    uniqueTowersMap.set(tower.name, tower);
                }
            });

            const uniqueTowers = Array.from(uniqueTowersMap.values());
            debugLog('Filtered unique towers:', uniqueTowers);
            return uniqueTowers.map(tower => processNodeData(tower, columns));
        }

        return [];
    }, [selectedNode, projectData, columns]);

    debugLog('Final render data:', {
        columnsLength: columns.length,
        locationDataLength: locationData.length,
        locationData
    });

    if (!columns.length) return null;

    return (
        <View style={styles.container}>
            <Table
                columns={columns}
                data={locationData}
                title="Location Details"
                tableName="locations"
                pageSize={10}
                menuAction={menuAction}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1
    }
});
